import { fetchQuery, graphql, type Environment } from 'react-relay';

import type { FomoCollaborator } from '../types';

import type { fetchUsersByAaidsQuery } from './__generated__/fetchUsersByAaidsQuery.graphql';

export type FetchUsersByAaidsResult = {
	collaborators: FomoCollaborator[];
	error: unknown | null;
};

/**
 * Batched resolve of multiple Atlassian Account IDs (AAIDs) to `FomoCollaborator[]`
 * via a single AGG GraphQL `users(accountIds:)` call.
 *
 * - Only requests `name` / `picture` on the `AtlassianAccountUser` subtype.
 *   `AppUser` and `CustomerUser` records resolve with no fields and are filtered out
 *   by the consumer, instead of rejecting the entire query.
 * - Preserves input order via `accountId` matching.
 * - Returns `{ collaborators: [], error }` when the fetch throws; `collaborators`
 *   may be partial when AGG returns only some users.
 */
export async function fetchUsersByAaids(
	environment: Environment,
	aaids: ReadonlyArray<string>,
): Promise<FetchUsersByAaidsResult> {
	if (aaids.length === 0) {
		return { collaborators: [], error: null };
	}
	try {
		const data = await fetchQuery<fetchUsersByAaidsQuery>(
			environment,
			graphql`
				query fetchUsersByAaidsQuery($accountIds: [ID!]!) {
					users(accountIds: $accountIds) {
						accountId
						name
						picture
					}
				}
			`,
			{ accountIds: aaids as string[] },
		).toPromise();

		const users = data?.users ?? [];
		const byAaid = new Map<string, FomoCollaborator>();
		for (const u of users) {
			// Skip records missing name or picture — we can't render an avatar without both.
			if (u?.accountId && u.name && u.picture) {
				byAaid.set(u.accountId, {
					accountId: u.accountId,
					displayName: u.name,
					avatarUrl: u.picture,
				});
			}
		}
		// Preserve input order.
		const collaborators = aaids.map((a) => byAaid.get(a)).filter((c): c is FomoCollaborator => !!c);
		return { collaborators, error: null };
	} catch (error) {
		return { collaborators: [], error };
	}
}
